// src/lib/partner/partnerCohortMetrics.js
import { supabase } from "../../supabaseClient";

function requireCohortId(cohortId) {
  const cleanId = String(
    cohortId || ""
  ).trim();

  if (!cleanId) {
    throw new Error(
      "A cohort ID is required."
    );
  }

  return cleanId;
}

function isMissingFunctionSignature(
  error
) {
  const code = String(
    error?.code || ""
  );

  const message = String(
    error?.message ||
      error?.details ||
      error?.hint ||
      ""
  ).toLowerCase();

  return (
    code === "PGRST202" ||
    code === "42883" ||
    message.includes(
      "could not find the function"
    ) ||
    message.includes(
      "function does not exist"
    )
  );
}

export async function fetchPartnerCohortRosterCounts() {
  const {
    data,
    error,
  } = await supabase.rpc(
    "get_my_partner_cohort_roster_counts"
  );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data
    : [];
}

export async function fetchPartnerCohortRosterCount(
  cohortId
) {
  const cleanCohortId =
    requireCohortId(cohortId);

  const rows =
    await fetchPartnerCohortRosterCounts();

  const row =
    rows.find(
      (item) =>
        String(
          item.cohort_id
        ) === cleanCohortId
    ) || null;

  /*
   * No row means there are currently
   * zero learner-only memberships
   * visible to this user's access.
   *
   * Never fall back to the legacy
   * cohort learner totals because those
   * can include Partner team accounts.
   */
  if (!row) {
    return {
      cohort_id:
        cleanCohortId,

      learner_count: 0,

      active_learner_count: 0,

      completed_learner_count: 0,
    };
  }

  return {
    cohort_id:
      row.cohort_id,

    learner_count:
      Number(
        row.learner_count || 0
      ),

    active_learner_count:
      Number(
        row.active_learner_count ||
          0
      ),

    completed_learner_count:
      Number(
        row.completed_learner_count ||
          0
      ),
  };
}

/*
 * Staff-safe learner roster.
 *
 * We use the backend roster that already
 * excludes Partner team members.
 *
 * The helper supports either of these RPC
 * signatures:
 *
 * get_my_partner_learner_roster()
 *
 * OR
 *
 * get_my_partner_learner_roster(
 *   p_cohort_id uuid
 * )
 *
 * This avoids tying the frontend to one
 * signature unnecessarily.
 */
export async function fetchPartnerCohortLearnerRoster(
  cohortId
) {
  const cleanCohortId =
    requireCohortId(cohortId);

  let response =
    await supabase.rpc(
      "get_my_partner_learner_roster"
    );

  if (
    response.error &&
    isMissingFunctionSignature(
      response.error
    )
  ) {
    response =
      await supabase.rpc(
        "get_my_partner_learner_roster",
        {
          p_cohort_id:
            cleanCohortId,
        }
      );
  }

  if (response.error) {
    throw response.error;
  }

  return (
    Array.isArray(response.data)
      ? response.data
      : []
  ).filter(
    (row) =>
      String(
        row.cohort_id
      ) === cleanCohortId
  );
}

export async function fetchPartnerCohortProgrammeFormat(
  cohortId
) {
  const cleanCohortId =
    requireCohortId(cohortId);

  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_cohort_programme_format",
    {
      p_cohort_id:
        cleanCohortId,
    }
  );

  if (error) {
    throw error;
  }

  return String(
    data || ""
  ).trim();
}

export async function updatePartnerCohortProgrammeFormat(
  cohortId,
  programmeFormat
) {
  const cleanCohortId =
    requireCohortId(cohortId);

  const cleanFormat =
    String(
      programmeFormat || ""
    ).trim();

  if (
    cleanFormat.length > 500
  ) {
    throw new Error(
      "Programme format must be 500 characters or fewer."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "update_partner_cohort_programme_format",
    {
      p_cohort_id:
        cleanCohortId,

      p_programme_format:
        cleanFormat || null,
    }
  );

  if (error) {
    throw error;
  }

  return String(
    data || ""
  ).trim();
}